import { CheckIcon, Copy } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";

import { Button, ButtonProps } from "./button";
import { Card } from "./card";

import { cn } from "@/lib/utils";

interface CopyButtonProps extends ButtonProps {
  value: string;
  src?: string;
}

/**
 * Copy a string to the clipboard
 * @param {string} value - The string to copy to the clipboard
 * @returns {Promise<void>} - A promise that resolves when the string has been copied to the clipboard
 */
export async function copyToClipboard(value: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    toast.success("Copied to clipboard");
  } catch (err) {
    toast.error("Failed to copy to clipboard");
  }
}

/**
 * A button that copies a string to the clipboard
 * @param {string} value - The string to copy to the clipboard
 * @param {string} className - The class name to apply to the button
 * @param {string} variant - The variant of the button
 * @returns {JSX.Element} - The copy button
 */
export function CopyButton({
  value,
  className,
  variant = "ghost",
  ...props
}: CopyButtonProps): JSX.Element {
  const [hasCopied, setHasCopied] = React.useState(false);

  React.useEffect(() => {
    setTimeout(() => {
      setHasCopied(false);
    }, 2000);
  }, [hasCopied]);

  return (
    <Button
      size='icon'
      variant={variant}
      className={cn("relative z-10 h-6 w-6 [&_svg]:h-3 [&_svg]:w-3", className)}
      onClick={async () => {
        await copyToClipboard(value);
        setHasCopied(true);
      }}
      {...props}
    >
      <span className='sr-only'>Copy</span>
      {hasCopied ? <CheckIcon /> : <Copy />}
    </Button>
  );
}

/**
 * A wrapper that adds a copy button to a component
 * @param {React.ReactNode} children - The children to wrap
 * @param {string} copyValue - The value to copy to the clipboard
 * @returns {JSX.Element} - The wrapped component
 */
export const CopyOnHover = ({
  children,
  copyValue,
  className,
}: {
  children: React.ReactElement;
  copyValue: string;
  className?: string;
}): JSX.Element => {
  return (
    <Card
      className={cn(
        "relative py-2 px-2 group border-0  cursor-pointer",
        className,
      )}
    >
      {children}
      <CopyButton
        value={copyValue}
        className='absolute top-0 bottom-0 -right-0 my-auto mx-0 '
      />
    </Card>
  );
};
